/**
 * newFloorplanSceneSignal.ts
 *
 * A one-shot marker for "the user just created this scene by choosing 'Draw
 * floorplan myself'", handed from UploadModal (which creates the scene) to
 * ScenesProvider (which decides the editor mode once the linked floorplan
 * loads, a navigation and a fetch later).
 *
 * That single case is the ONLY one that should open in plan mode: the scene
 * holds nothing but a seeded template room, and the template picker is about to
 * open over it. Every other scene with a floorplan or roomscan opens in edit
 * mode — including one that happens to contain no objects yet, which used to be
 * mistaken for "brand new" and dropped the user into plan mode on every visit.
 *
 * Deliberately not a URL parameter (the sibling `?startEditor=true` convention
 * would have been the obvious place): a parameter lingers in the address bar and
 * would re-open plan mode on every reload of that URL, which is exactly the
 * "brand new" claim it is meant to make only once.
 *
 * Keyed by shortid so a scene created in one tab-session cannot put a
 * differently-chosen scene into plan mode.
 */

let pendingShortid: string | null = null;

/** Record that `shortid` was just created through the floorplanner flow. */
export function markSceneCreatedInFloorplanner(shortid: string): void {
	pendingShortid = shortid;
}

/**
 * Whether `shortid` is the scene most recently created through the floorplanner
 * flow. True at most once per creation — the marker is cleared on read.
 */
export function consumeSceneCreatedInFloorplanner(
	shortid: string | undefined | null,
): boolean {
	if (!shortid || pendingShortid !== shortid) {
		return false;
	}
	pendingShortid = null;
	return true;
}
